/**
 * Wires up the workspace-related side effects that run once the user's CMMD
 * account preferences are loaded:
 *
 * - Seeds `currentForgeWorkspaceId` from `lastActiveOrganizationId` (read from
 *   CMMD preferences) so the local session starts on the right workspace.
 * - When that preference is absent, seeds the same store from a deterministic
 *   fact instead of leaving it null: the account's own runtime organization,
 *   or its sole membership when it has only one.
 * - Configures the inventory resource's passive refresh scope so it narrows
 *   requests to the current workspace rather than fetching all workspaces.
 * - Writes `lastActiveOrganizationId` back to CMMD on an explicit switch, and
 *   once as a backfill when the preferences load with nothing stored yet but
 *   the local session already has a confirmed workspace.
 *
 * Both the deterministic seed and the backfill close CMMD-Center/forge#3572's
 * remaining gaps.
 *
 * The startup seed matters because `useFirstRunEnvironment` (the first-run
 * "ask" step) only mounts on `/setup`, and only calls `seedCurrentForgeWorkspaceId`
 * from its `ensure()` click. An account that already had an Environment before
 * this store existed, or before CMMD-Center/forge#3572 PR1 shipped, never
 * lands on `/setup` again and never calls `ensure()` again, so without this
 * effect its local store stays null on every browser forever: the backfill
 * below needs a confirmed value to write, and this is the only place that can
 * supply one for such an account.
 *
 * The backfill matters because the first-run "ask" step's own confirm calls
 * `seedCurrentForgeWorkspaceId`, never `switchCurrentForgeWorkspaceId`
 * (`useFirstRunEnvironment.ts`'s `ensure()`), so it never bumped
 * `switchGeneration` and the persist below never fired for it. Production
 * evidence, 2026-09-27: the owner's explicit "Use workspace CMMD" confirm
 * scoped every later request to org 2119 correctly, but three
 * `GET /api/cmmd/account/preferences` calls that session never returned
 * `lastActiveOrganizationId`, because nothing had ever PATCHed it.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import {
  type CmmdForgeRuntimeListSnapshot,
  useCmmdForgeRuntimeListSnapshot,
} from "../cmmd/cmmdForgeRuntimeListResource";
import { configureRuntimeInventoryRefreshScope } from "../cmmd/cmmdRuntimeInventoryResource";
import { persistLastActiveOrganizationId } from "../cmmd/forgeAccountPreferences";
import { isCmmdEmbedMode } from "../embed/cmmdEmbedMode";
import { createCmmdApiClient } from "../environments/cmmdApi";
import {
  getCurrentForgeWorkspaceId,
  seedCurrentForgeWorkspaceId,
  useCurrentForgeWorkspaceState,
} from "../workspace/currentForgeWorkspaceStore";
import { resolveCurrentForgeWorkspace } from "../workspace/resolveCurrentForgeWorkspace";
import { resolveOwnRuntimeOrganizationId } from "../workspace/resolveOwnRuntimeOrganizationId";

/**
 * Shares `ForgeWorkspaceSwitcher.tsx`'s bootstrap query key on purpose: both
 * read the same `/api/cmmd/bootstrap` response, and the switcher already
 * fetches it on every authenticated non-setup page, so this rarely issues a
 * second request.
 */
const BOOTSTRAP_QUERY_KEY = ["forge-workspace-switcher", "bootstrap"] as const;

/**
 * A catalog fetch that hasn't actually settled (still the pre-fetch initial
 * snapshot, or a failed/auth-required load) reports the same empty shape a
 * real "no Environment yet" account does (CMMD-Center/forge#3572 section 3),
 * so this must be true before trusting `environments` for a startup guess.
 * The identical check lives as `isCatalogSettled` in
 * `useFirstRunEnvironment.logic.ts` for the same resource; duplicated here
 * rather than imported, since it is three lines and importing it would pull
 * this route module into the onboarding component tree's dependency graph
 * for no shared behavior otherwise.
 */
function isRuntimeCatalogSettled(catalog: CmmdForgeRuntimeListSnapshot): boolean {
  if (!catalog.connected) return false;
  if (catalog.error !== null) return false;
  return catalog.fetchedAt !== null;
}

export function useWorkspaceSideEffects(
  lastActiveOrganizationId: number | null | undefined,
  preferencesLoaded = false,
): void {
  // Seed the current workspace from the CMMD preference on first load.
  useEffect(() => {
    if (lastActiveOrganizationId == null) return;
    seedCurrentForgeWorkspaceId(lastActiveOrganizationId);
  }, [lastActiveOrganizationId]);

  // The preference is absent: seed a deterministic fact instead of leaving
  // the store null. Only runs once preferences have loaded and confirmed
  // there is nothing stored, the store itself is still empty (an explicit
  // switch or the first-run "ask" step's confirm always wins), and the
  // account's own runtime catalog has actually finished loading. An
  // unsettled catalog reports the same empty shape a real "no Environment
  // yet" account does (CMMD-Center/forge#3572 section 3), so a startup guess
  // off it would be exactly the bug this closes.
  const needsDeterministicSeed = preferencesLoaded && lastActiveOrganizationId == null;
  const runtimeCatalog = useCmmdForgeRuntimeListSnapshot({ enabled: needsDeterministicSeed });
  const bootstrapForSeed = useQuery({
    queryKey: BOOTSTRAP_QUERY_KEY,
    queryFn: () => createCmmdApiClient().getBootstrap(),
    staleTime: 30_000,
    enabled: needsDeterministicSeed && !isCmmdEmbedMode(),
  });
  useEffect(() => {
    if (!needsDeterministicSeed) return;
    if (isCmmdEmbedMode()) return;
    if (getCurrentForgeWorkspaceId() !== null) return;
    if (!isRuntimeCatalogSettled(runtimeCatalog)) return;
    const bootstrap = bootstrapForSeed.data;
    if (!bootstrap) return;

    const resolution = resolveCurrentForgeWorkspace({
      storedWorkspaceId: resolveOwnRuntimeOrganizationId({
        userId: bootstrap.identity.id,
        environments: runtimeCatalog.environments,
      }),
      workspaces: bootstrap.workspaces,
    });
    // Ambiguous (more than one membership, no runtime fact to break the tie)
    // or zero memberships: leave the store null. The first-run "ask" step or
    // the shell switcher owns asking; this effect has no UI of its own.
    if (resolution.kind !== "resolved") return;
    seedCurrentForgeWorkspaceId(resolution.organizationId);
  }, [needsDeterministicSeed, runtimeCatalog, bootstrapForSeed.data]);

  // Wire the inventory resource's passive refresh scope to the current workspace.
  // A single call at mount is enough: getRefreshScope reads the store synchronously
  // at each refresh, so the getter stays current without re-configuring.
  const scopeConfiguredRef = useRef(false);
  if (!scopeConfiguredRef.current) {
    scopeConfiguredRef.current = true;
    configureRuntimeInventoryRefreshScope(() => {
      const orgId = getCurrentForgeWorkspaceId();
      return orgId !== null ? { organizationId: orgId } : { allWorkspaces: true };
    });
  }

  // Persist the active workspace back to CMMD in exactly two cases:
  //
  // - An explicit switch (switchGeneration > 0, the shell switcher).
  // - A backfill: preferences finished loading and carried no value, but the
  //   local session already has a confirmed workspace (the deterministic seed
  //   above, the first-run "ask" step's own seed, an embed handoff, or a
  //   pre-existing localStorage pick from before this backfill existed).
  //   Gating the backfill on `preferencesLoaded` keeps the 84265a4d4a fix
  //   intact: a value read on mount before the fetch resolves never counts,
  //   only a fetch that actually completed and came back empty.
  //
  // Embed sessions never persist here: CMMD owns the workspace it handed the
  // iframe (`cmmdEmbedWorkspaceBind.ts`), and writing it back would turn a
  // one-off handoff into the user's standalone preference.
  //
  // `lastPersistedIdRef` stops a second PATCH for a value already sent this
  // session. The case this actually guards, proven in the test suite: an
  // explicit switch persists id A, then the preference read refetches and
  // still names A (the round trip completing), and `lastActiveOrganizationId`
  // is a dependency of this effect, so it reruns with `isExplicitSwitch`
  // still true from the unchanged `switchGeneration`. Only the ref stops a
  // second PATCH of the same value in that rerun.
  //
  // The ref is claimed synchronously, before `persistLastActiveOrganizationId`
  // resolves, so that immediate rerun sees it already claimed and does not
  // fire a second PATCH while the first is still in flight. But a claim is
  // provisional: if the write never actually lands (`persistLastActiveOrganizationId`
  // itself retries a bounded number of times and then gives up), the claim is
  // released. Recording the id as done before confirming it was written would
  // suppress that org id forever, since nothing else would ever change this
  // effect's dependencies again on its own (CodeRabbit review, CMMD-Center/forge#3572).
  const workspaceState = useCurrentForgeWorkspaceState();
  const lastPersistedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (isCmmdEmbedMode()) return;
    const orgId = workspaceState.workspaceId;
    if (orgId == null) return;
    if (lastPersistedIdRef.current === orgId) return;

    const isExplicitSwitch = workspaceState.switchGeneration > 0;
    const isBackfill = preferencesLoaded && lastActiveOrganizationId == null;
    if (!isExplicitSwitch && !isBackfill) return;

    lastPersistedIdRef.current = orgId;
    void persistLastActiveOrganizationId(orgId).then((succeeded) => {
      if (!succeeded && lastPersistedIdRef.current === orgId) {
        lastPersistedIdRef.current = null;
      }
    });
  }, [
    workspaceState.switchGeneration,
    workspaceState.workspaceId,
    preferencesLoaded,
    lastActiveOrganizationId,
  ]);
}
